import { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'

import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Pagination from '@mui/material/Pagination'
import Paper from '@mui/material/Paper'
import Snackbar from '@mui/material/Snackbar'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import {
  AddIcon,
  AssignmentIcon,
  DeleteIcon,
  ExpandIcon,
  LaunchIcon,
} from 'src/v3/core/icons'

import theme from 'src/theme'
import axios from 'src/utilities/http-client'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import SelectLinkedService from 'src/v3/manage/components/linked-service'
import { Assignment } from 'src/v3/core/types'
import { maxListWidth } from 'src/v3/manage/constants'
import ConfirmAction from 'src/v3/components/confirm-action'
import Loading from 'src/v3/components/loading'
import {
  selectAssignmentsByLinkedService,
  selectAssignmentsStatus,
  fetchAssignments,
  selectActiveLinkedService,
  selectCompletedAssignmentsByLinkedService,
  selectCompletedAssignmentsStatus,
  fetchCompletedAssignments,
  selectUsers,
  selectUsersStatus,
  fetchUsers,
} from '../manage.slice'

let deleteAssignment = (assignmentId: string) =>
  axios.delete<unknown>(`/api/v2/manage/assignment?id=${assignmentId}`)

type AssignmentFilter = 'completed' | 'current' | 'all'

export default (): ReactElement => {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()

  let activeLinkedService = useAppSelector(selectActiveLinkedService)
  let assignments = useAppSelector(
    selectAssignmentsByLinkedService(activeLinkedService),
  )
  let assignmentsStatus = useAppSelector(selectAssignmentsStatus)
  let completedAssignments = useAppSelector(
    selectCompletedAssignmentsByLinkedService(activeLinkedService),
  )
  let completedAssignmentsStatus = useAppSelector(
    selectCompletedAssignmentsStatus,
  )
  let users = useAppSelector(selectUsers)
  let usersStatus = useAppSelector(selectUsersStatus)

  let [page, setPage] = useState<number>(1)
  let [nameFilter, setNameFilter] = useState<string>('')
  let [filterTab, setFilterTab] = useState<AssignmentFilter>('current')
  let [dialogStatus, setDialogStatus] = useState<string>('')
  let [errorMessage, setErrorMessage] = useState<string>('')

  let filteredAssignments: Record<AssignmentFilter, Assignment[]> = {
    completed: assignments.filter(
      (a) =>
        a.users.length &&
        a.users.every((u) =>
          completedAssignments.some(
            (ca) => a.id === ca.assignmentID && ca.userID === u,
          ),
        ),
    ),
    current: assignments.filter(
      (a) =>
        !a.users.length ||
        !a.users.every((u) =>
          completedAssignments.some(
            (ca) => a.id === ca.assignmentID && ca.userID === u,
          ),
        ),
    ),
    all: [...assignments],
  }

  let visibleAssignments = filteredAssignments[filterTab].filter((a) =>
    a.name.toLowerCase().includes(nameFilter.toLowerCase()),
  )

  let overlayStyle = {
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  let [errTitle, errMsg] = errorMessage.split(':')

  let handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value)
  }

  let checkPagination = () => {
    if (visibleAssignments.length <= (page - 1) * 10) {
      setPage(page - 1 || 1)
    }
  }

  useEffect(() => {
    if (assignmentsStatus === 'uninitialized') {
      dispatch(fetchAssignments())
    }
  }, [assignmentsStatus])

  useEffect(() => {
    if (completedAssignmentsStatus === 'uninitialized') {
      dispatch(fetchCompletedAssignments())
    }
  }, [completedAssignmentsStatus])

  useEffect(() => {
    if (usersStatus === 'uninitialized') {
      dispatch(fetchUsers())
    }
  }, [usersStatus])

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          p: 2,
          maxWidth: maxListWidth,
          bgcolor: 'background.paper',
          margin: 'auto',
        }}
      >
        <div className="flex items-center justify-center my-2">
          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
            <AssignmentIcon />
          </Avatar>
          <Typography
            variant="h4"
            className="pl-2"
            style={{
              fontFamily: 'smileAndWave',
            }}
          >
            Assignments
          </Typography>
        </div>
        <div className="flex justify-center">
          <Typography
            paragraph
            className="px-2 d-inline"
            color="text.secondary"
          >
            Create, view, edit, and delete assignments
          </Typography>
        </div>
        <SelectLinkedService linkedServiceID={activeLinkedService} />
        <Box>
          <Tabs
            sx={{ marginBottom: -1 }}
            variant="fullWidth"
            value={filterTab}
            onChange={(e, num) => {
              setFilterTab(num)
              checkPagination()
            }}
            centered
          >
            <Tab value="current" label="Current" />
            <Tab value="completed" label="Completed" />
            <Tab value="all" label="all" />
          </Tabs>
          <Paper variant="outlined" elevation={0} className="mt-2 mb-4">
            <Accordion disableGutters>
              <AccordionSummary expandIcon={<ExpandIcon />}>
                Filters
              </AccordionSummary>
              <AccordionDetails
                className="flex px-3 pt-0 justify-content-between"
                sx={{
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <TextField
                  variant="outlined"
                  label="Name"
                  name="name"
                  type="text"
                  size="small"
                  value={nameFilter}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setNameFilter(event.target.value)
                    checkPagination()
                  }}
                  sx={{ flexBasis: 150, flexGrow: 1, flexShrink: 1 }}
                />
              </AccordionDetails>
            </Accordion>
            <List
              sx={{
                overflow: 'auto',
                width: '100%',
                backgroundColor: 'background.paper',
                minHeight: 247,
                height:
                  filteredAssignments[filterTab].length >= 10
                    ? 620
                    : filteredAssignments[filterTab].length * 62,
              }}
              disablePadding
            >
              {visibleAssignments
                .slice(10 * (page - 1), 10 * page)
                .map((assignment) => {
                  let assignmentUsers = users.filter((u) =>
                    assignments
                      .find((a) => a.id === assignment.id)
                      ?.users.some((au) => au === u.id),
                  )
                  let completedUsers = assignmentUsers.filter((au) =>
                    completedAssignments.some(
                      (ca) =>
                        ca.assignmentID === assignment.id &&
                        ca.userID === au.id,
                    ),
                  )
                  let percentage = Math.round(
                    (100 * completedUsers.length) / assignmentUsers.length,
                  )

                  return (
                    <ListItem
                      key={assignment.id}
                      secondaryAction={
                        <>
                          <Tooltip title="View">
                            <IconButton
                              aria-label="view"
                              onClick={() =>
                                navigate(`/manage/assignments/${assignment.id}`)
                              }
                            >
                              <LaunchIcon />
                            </IconButton>
                          </Tooltip>
                          <>
                            <Tooltip title="Delete">
                              <IconButton
                                aria-label="delete"
                                onClick={() => {
                                  setDialogStatus(`delete-${assignment.id}`)
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                            <ConfirmAction
                              executeAction={() =>
                                deleteAssignment(assignment.id).then(() =>
                                  dispatch(fetchAssignments()),
                                )
                              }
                              open={dialogStatus}
                              setOpen={setDialogStatus}
                              openText={`delete-${assignment.id}`}
                              title="Confirm Deletion"
                              text={`Are you sure you want to delete ${assignment.name}?`}
                              onFailure={(err) =>
                                setErrorMessage(
                                  `Error Deleting ${assignment.name}: ${err}`,
                                )
                              }
                            />
                          </>
                        </>
                      }
                      sx={{ pr: 12 }}
                      divider
                      disablePadding
                    >
                      <Tooltip
                        title={`${completedUsers.length}/${assignmentUsers.length} Users Complete`}
                        placement="left"
                      >
                        <span style={{ marginLeft: 4, marginRight: 4 }}>
                          <IconButton disabled>
                            <Box sx={{ position: 'relative', display: 'flex' }}>
                              <CircularProgress
                                color="inherit"
                                variant="determinate"
                                value={100}
                                size={36}
                              />
                              <Box sx={overlayStyle}>
                                <CircularProgress
                                  color={
                                    percentage === 100 ? 'success' : 'primary'
                                  }
                                  variant="determinate"
                                  value={
                                    Number.isNaN(percentage) ? 0 : percentage
                                  }
                                  size={36}
                                />
                              </Box>
                              <Box sx={overlayStyle}>
                                <Typography
                                  color="text.primary"
                                  sx={{ fontSize: 11 }}
                                >
                                  {Number.isNaN(percentage)
                                    ? 'N/A'
                                    : `${Math.round(percentage)}%`}
                                </Typography>
                              </Box>
                            </Box>
                          </IconButton>
                        </span>
                      </Tooltip>
                      <ListItemText
                        primary={assignment.name}
                        secondary={`Due: ${dayjs(assignment.dueDate).format('MMMM DD, YYYY')}; Assigned On: ${dayjs(assignment.createdTime).format('MMMM DD, YYYY')}`}
                        secondaryTypographyProps={{ sx: { fontSize: 12 } }}
                        sx={{ py: 0.5 }}
                      />
                    </ListItem>
                  )
                })}
            </List>
            <Divider />
            <Pagination
              size="small"
              sx={{
                paddingY: '5px',
                display: 'flex',
                justifyContent: 'center',
              }}
              count={Math.ceil(visibleAssignments.length / 10)}
              page={page}
              onChange={handlePageChange}
              showFirstButton
              showLastButton
            />
            <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
              <Alert
                variant="filled"
                onClose={() => setErrorMessage('')}
                severity="error"
              >
                <strong>{errTitle}:</strong>
                {errMsg}
              </Alert>
            </Snackbar>
          </Paper>
          <div className="flex justify-center mt-4">
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              disabled={activeLinkedService === 'unselected'}
              onClick={() => {
                navigate('/manage/assignments/create')
              }}
            >
              Create
            </Button>
          </div>
        </Box>
      </Card>
      <Loading
        entities={[assignmentsStatus, completedAssignmentsStatus, usersStatus]}
      />
    </>
  )
}
